import { useState, useEffect } from "react";
import MetricCard from "../components/MetricCard";
import { getStatsOverview, getStatsHealth } from "../api/adminApi";
import type { StatsOverviewResponse, StatsHealthResponse } from "../types/admin";
import { formatDateTime, TIMEZONE_LABEL } from "../utils/timezone";

/**
 * Health Indicator Component
 */
function HealthIndicator({
  label,
  status,
}: {
  label: string;
  status: "healthy" | "warning" | "critical";
}) {
  const statusConfig = {
    healthy: { color: "var(--accent-success)", icon: "✓", bg: "rgba(34, 197, 94, 0.1)" },
    warning: { color: "var(--accent-warning)", icon: "⚠", bg: "rgba(234, 179, 8, 0.1)" },
    critical: { color: "var(--accent-danger)", icon: "✕", bg: "rgba(239, 68, 68, 0.1)" },
  };

  const config = statusConfig[status];

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        padding: "0.75rem 1rem",
        backgroundColor: config.bg,
        borderRadius: "0.5rem",
        border: `1px solid ${config.color}30`,
      }}
    >
      <span
        style={{
          width: "24px",
          height: "24px",
          borderRadius: "50%",
          backgroundColor: config.color,
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.75rem",
          fontWeight: "bold",
        }}
      >
        {config.icon}
      </span>
      <span style={{ color: "#f1f5f9" }}>{label}</span>
    </div>
  );
}

function DashboardHome() {
  const [data, setData] = useState<StatsOverviewResponse | null>(null);
  const [healthData, setHealthData] = useState<StatsHealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
    // Auto-refresh every 60 seconds
    const interval = setInterval(loadData, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      setError(null);
      const [overviewResult, healthResult] = await Promise.all([
        getStatsOverview(),
        getStatsHealth(),
      ]);
      setData(overviewResult);
      setHealthData(healthResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-message">
        <p>{error}</p>
        <button onClick={loadData} style={{ marginTop: "1rem" }}>
          Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  const formatMrr = (cents: number) => {
    return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0 })}`;
  };

  const formatPercentage = (rate: number) => {
    return `${(rate * 100).toFixed(1)}%`;
  };

  // Normalize circuit breaker states (backend may return uppercase)
  const normalizedMainCBState = healthData?.circuitBreaker?.state?.toLowerCase() || "unknown";
  const normalizedAuditCBState = healthData?.auditCircuitBreaker?.state?.toLowerCase() || "unknown";

  return (
    <div>
      <header className="page-header">
        <h1 className="page-title">Dashboard Overview</h1>
        <p className="page-subtitle">
          {data.period.start} to {data.period.end} •{" "}
          <span className={`env-badge ${data.system.environment}`}>{data.system.environment}</span>
        </p>
      </header>

      {/* Health Summary - At top for quick overview */}
      {healthData && (
        <div className="card" style={{ marginBottom: "1.5rem" }}>
          <div className="card-header">
            <h3 className="card-title">Health Summary</h3>
          </div>
          <div className="card-body">
            <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", justifyContent: "center" }}>
              <HealthIndicator
                label="Main Circuit Breaker"
                status={normalizedMainCBState === "closed" ? "healthy" : normalizedMainCBState === "half-open" ? "warning" : "critical"}
              />
              <HealthIndicator
                label="Audit Circuit Breaker"
                status={normalizedAuditCBState === "closed" ? "healthy" : normalizedAuditCBState === "half-open" ? "warning" : "critical"}
              />
              <HealthIndicator
                label="Audit Sync Buffer"
                status={
                  healthData.auditSyncBuffer.entryCount === 0 ? "healthy" :
                  healthData.auditSyncBuffer.entryCount > 1000 ? "critical" : "warning"
                }
              />
              <HealthIndicator
                label="Audit DLQ"
                status={
                  healthData.auditDLQ.totalReceived === 0 ? "healthy" :
                  healthData.auditDLQ.failedPermanentlyCount > 0 ? "critical" : "warning"
                }
              />
              <HealthIndicator
                label="Main Database"
                status={healthData.databases.main.status === "healthy" ? "healthy" : healthData.databases.main.status === "slow" ? "warning" : "critical"}
              />
              {healthData.databases.pii && (
                <HealthIndicator
                  label="PII Database"
                  status={healthData.databases.pii.status === "healthy" ? "healthy" : healthData.databases.pii.status === "slow" ? "warning" : "critical"}
                />
              )}
              <HealthIndicator
                label="Audit Database"
                status={healthData.databases.audit.status === "healthy" ? "healthy" : healthData.databases.audit.status === "slow" ? "warning" : "critical"}
              />
              {healthData.databases.invites && (
                <HealthIndicator
                  label="Invites Database"
                  status={healthData.databases.invites.status === "healthy" ? "healthy" : healthData.databases.invites.status === "slow" ? "warning" : "critical"}
                />
              )}
              {healthData.databases.analytics && (
                <HealthIndicator
                  label="Analytics Database"
                  status={healthData.databases.analytics.status === "healthy" ? "healthy" : healthData.databases.analytics.status === "slow" ? "warning" : "critical"}
                />
              )}
              {healthData.r2Bucket && (
                <HealthIndicator
                  label="R2 Audit Bucket"
                  status={healthData.r2Bucket.status === "healthy" ? "healthy" : healthData.r2Bucket.status === "slow" ? "warning" : "critical"}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* System Status Banner - Only show when either circuit breaker is OPEN */}
      {(normalizedMainCBState !== "closed" || normalizedAuditCBState !== "closed") && (
        <div
          className="card"
          style={{
            background: "rgba(239, 68, 68, 0.1)",
            borderColor: "var(--accent-danger)",
            marginBottom: "1.5rem",
          }}
        >
          <div className="card-body" style={{ textAlign: "center" }}>
            ⚠️ Circuit Breaker Status:{" "}
            {normalizedMainCBState !== "closed" && (
              <span>
                Main D1: <strong>{normalizedMainCBState.toUpperCase()}</strong>
                {normalizedAuditCBState !== "closed" && " | "}
              </span>
            )}
            {normalizedAuditCBState !== "closed" && (
              <span>
                Audit D1: <strong>{normalizedAuditCBState.toUpperCase()}</strong>
              </span>
            )}
            {" "}- D1 writes are being queued
          </div>
        </div>
      )}

      {/* User Metrics */}
      <h2 style={{ fontSize: "1rem", marginBottom: "1rem", color: "var(--text-secondary)" }}>
        👥 Users
      </h2>
      <div className="metrics-grid">
        <MetricCard label="Total Users" value={data.users.total} />
        <MetricCard label="Active Users" value={data.users.active} />
        <MetricCard
          label="New Users"
          value={data.users.new}
          change={{ value: 0, label: "in period" }}
        />
        <MetricCard label="DAU Today" value={data.system.dauToday} />
      </div>

      {/* Revenue Metrics */}
      <h2 style={{ fontSize: "1rem", marginBottom: "1rem", color: "var(--text-secondary)" }}>
        💰 Revenue
      </h2>
      <div className="metrics-grid">
        <MetricCard label="MRR" value={formatMrr(data.subscriptions.mrrUsdCents)} />
        <MetricCard label="Premium" value={data.subscriptions.premium} />
        <MetricCard label="Standard" value={data.subscriptions.standard} />
        <MetricCard label="Basic" value={data.subscriptions.basic} />
      </div>

      {/* Invite Metrics */}
      <h2 style={{ fontSize: "1rem", marginBottom: "1rem", color: "var(--text-secondary)" }}>
        📨 Invites
      </h2>
      <div className="metrics-grid">
        <MetricCard label="Invites Sent" value={data.invites.sent} />
        <MetricCard label="Accepted" value={data.invites.accepted} />
        <MetricCard label="Conversion Rate" value={`${data.invites.conversionRate}%`} />
        <MetricCard
          label="K-Factor"
          value={data.invites.kFactor ? data.invites.kFactor.toFixed(2) : "N/A"}
        />
      </div>

      {/* SMS Metrics */}
      <h2 style={{ fontSize: "1rem", marginBottom: "1rem", color: "var(--text-secondary)" }}>
        📱 SMS (Last 24h)
      </h2>
      <div className="metrics-grid">
        <MetricCard label="Sent" value={data.sms.sent} />
        <MetricCard label="Delivered" value={data.sms.delivered} />
        <MetricCard label="Failed" value={data.sms.failed} emphasis={data.sms.failed > 0 ? "danger" : "default"} />
        <MetricCard label="Delivery Rate" value={formatPercentage(data.sms.deliveryRate)} />
      </div>

      {/* System Status */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">System Status</h3>
        </div>
        <div className="card-body">
          <table className="data-table">
            <tbody>
              <tr>
                <td>Environment</td>
                <td>
                  <span className={`env-badge ${data.system.environment}`}>
                    {data.system.environment}
                  </span>
                </td>
              </tr>
              <tr>
                <td>Worker Version</td>
                <td>{data.system.workerVersion || "Unknown"}</td>
              </tr>
              <tr>
                <td>Last Updated</td>
                <td>{formatDateTime(data.timestamp)} {TIMEZONE_LABEL}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default DashboardHome;
